import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { DialogShell, type ConfirmOptions } from '../shell/dialogs';

export type { ConfirmOptions };

export function useConfirm(): { confirm: (o: ConfirmOptions) => Promise<boolean>; dialog: ReactNode } {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  const dialog = state ? (
    <DialogShell eyebrow="Potvrzení" title={state.title} onClose={() => close(false)}>
      <div className="mailing-dialog-body">
        {state.message && <p>{state.message}</p>}
        <div className="mailing-dialog-actions">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => close(false)}>Zrušit</button>
          <button type="button" className={`btn btn-sm ${state.danger ? 'btn-danger' : 'btn-primary'}`} autoFocus onClick={() => close(true)}>{state.confirmLabel ?? 'Potvrdit'}</button>
        </div>
      </div>
    </DialogShell>
  ) : null;
  return { confirm, dialog };
}

export function isValidLink(url: string): boolean {
  const u = url.trim();
  return /^https:\/\/[^\s"<>]+\.[^\s"<>]+/i.test(u) || /^mailto:[^\s"<>@]+@[^\s"<>]+$/i.test(u) || /^\[%\s*shared\.[A-Z0-9_]+\s*%\]$/.test(u);
}

export function LinkDialog({ initial, onSubmit, onClose }: { initial?: string; onSubmit: (url: string) => void; onClose: () => void }) {
  const [value, setValue] = useState(initial ?? 'https://');
  const [touched, setTouched] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); input.current?.select(); }, []);
  const ok = isValidLink(value);
  const submit = () => { setTouched(true); if (ok) onSubmit(value.trim()); };
  return (
    <DialogShell eyebrow="Odkaz" title="Vložit odkaz" onClose={onClose}>
      <form className="mailing-dialog-body" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="mailing-dialog-field">
          <span>Adresa odkazu</span>
          <input ref={input} className={`form-control${touched && !ok ? ' is-invalid' : ''}`} value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://… nebo mailto:…" />
        </label>
        {touched && !ok && <div className="mailing-dialog-error">Odkaz musí začínat https:// nebo mailto:, případně to může být proměnná [% shared.… %].</div>}
        <div className="mailing-dialog-actions">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Zrušit</button>
          <button type="submit" className="btn btn-sm btn-primary">Vložit odkaz</button>
        </div>
      </form>
    </DialogShell>
  );
}

export function NoticeDialog({ title, message, onClose }: { title: string; message: ReactNode; onClose: () => void }) {
  return (
    <DialogShell eyebrow="Upozornění" title={title} onClose={onClose}>
      <div className="mailing-dialog-body">
        <p>{message}</p>
        <div className="mailing-dialog-actions">
          <button type="button" className="btn btn-sm btn-primary" autoFocus onClick={onClose}>Rozumím</button>
        </div>
      </div>
    </DialogShell>
  );
}
